/**
 * Group a variant's form fields into sections.
 *
 * The forms outgrew a flat list: PostgreSQL HA has 14 fields and MongoDB ~22, rendered as one
 * unbroken column of label-and-input. A DBA scanning for "storage class" had to read every
 * label, and there was no visual difference between a field that changes the topology and one
 * that sets a password.
 *
 * Grouping is derived from field NAMES rather than declared per variant, so a new field lands
 * in the right section automatically and nothing has to be kept in sync with the backend.
 */

// Order matters — it is the order sections appear.
const GROUPS = [
  {
    id: 'identity',
    title: 'Identity',
    hint: 'Names the resources and the namespace they live in.',
    match: (k) => ['project', 'namespace', 'db_name', 'db_user', 'admin_username', 'image'].includes(k),
  },
  {
    id: 'credentials',
    title: 'Credentials',
    hint: 'Leave blank to generate a strong password — you can read it back from Connection.',
    match: (k) => k.includes('password'),
  },
  {
    id: 'topology',
    title: 'Topology',
    hint: 'How many members, and how they are arranged. Changes the shape of the cluster.',
    match: (k) => ['replica_count', 'slave_count', 'node_count', 'shard_count',
                   'members_per_shard', 'use_arbiter', 'config_count', 'router_count'].includes(k),
  },
  {
    id: 'placement',
    title: 'Placement',
    hint: 'Which nodes to run on. Blank means Kubernetes decides.',
    match: (k) => k === 'role' || k === 'worker_role' || k.endsWith('_role'),
  },
  {
    id: 'resources',
    title: 'Storage & resources',
    hint: 'Disk per member, and the CPU/memory each pod requests.',
    match: (k) => k.startsWith('storage') || k.includes('_cpu_') || k.includes('_memory_')
                  || ['memory', 'jvm'].includes(k),
  },
  {
    id: 'access',
    title: 'External access',
    hint: 'Optional — expose the admin GUI on a domain.',
    match: (k) => k === 'domain' || k.includes('ingress'),
  },
];

/**
 * @returns [{id, title, hint, fields: [[key, spec], ...]}] — only non-empty sections, with
 * anything unmatched collected into a trailing "Other" group so no field is ever dropped.
 */
export function groupFields(form) {
  const entries = Object.entries(form || {});
  const taken = new Set();
  const out = [];

  for (const g of GROUPS) {
    const fields = entries.filter(([k]) => !taken.has(k) && g.match(k));
    fields.forEach(([k]) => taken.add(k));
    if (fields.length) out.push({ ...g, fields });
  }

  // A field that matches nothing still has to render — silently hiding it would be worse
  // than an untidy section.
  const rest = entries.filter(([k]) => !taken.has(k));
  if (rest.length) {
    out.push({ id: 'other', title: 'Other', hint: '', fields: rest });
  }
  return out;
}

/** Compact fields (ints, bools, short selects) can sit two-per-row without cramping. */
export function isNarrow(spec) {
  return spec.type === 'int' || spec.type === 'bool' || spec.type === 'select';
}
